import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLiquidityScenarioHeads,
  getLiquidityScenarioById,
  createLiquidityScenario,
  updateLiquidityScenarioHead,
  updateLiquidityScenarioTransactions,
  deleteLiquidityScenarioById
} from '@/services/liquidity-api';
import {
  LiquidityScenarioHead,
  LiquidityScenario,
  NewLiquidityScenario,
  UpdateLiquidityScenarioHead,
  LiquidityScenarioTransaction
} from '@/types/liquidity-api';

// Fetch all liquidity scenarios
export const useLiquidityScenarioHeads = (clientId: string, connectionId: string) => {
  const {data: liquidityScenarioHeads, isLoading: isLoadingLiquidityScenarioHeads, error: liquidityScenarioHeadsError } =  useQuery<Array<LiquidityScenarioHead>>({
    queryKey: ['liquidityScenarioHeads', clientId, connectionId],
    queryFn: () => getLiquidityScenarioHeads(clientId, connectionId),
		enabled: Boolean(clientId && connectionId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

	return {liquidityScenarioHeads, isLoadingLiquidityScenarioHeads, liquidityScenarioHeadsError}
};

// Fetch a specific liquidity scenario by ID
export const useLiquidityScenario = (liquidityScenarioId: string | null) => {
    const {data: selectedLiquidityScenario, isLoading: isLoadingScenario, error: scenarioError} = useQuery<LiquidityScenario>({
      queryKey: ['liquidityScenario', liquidityScenarioId],
      queryFn: () =>  getLiquidityScenarioById(liquidityScenarioId),
      enabled: Boolean(liquidityScenarioId), // Only run query when we have an ID
      staleTime: 0, // Always consider data stale
      gcTime: 0, // Don't cache the data
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
    return {selectedLiquidityScenario, isLoadingScenario, scenarioError};
};

// Create a new liquidity scenario
export const useCreateLiquidityScenario = (clientId: string, connectionId: string) => {
	const queryClient = useQueryClient();

  const { mutate: createNewLiquidityScenario, isPending: isCreating, isSuccess: createSuccess, error: createError } = useMutation<LiquidityScenario, Error, NewLiquidityScenario>({
    mutationFn: (newLiquidityScenario: NewLiquidityScenario) => createLiquidityScenario(newLiquidityScenario),
			onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['liquidityScenarioHeads', clientId, connectionId] }); // Refresh list
			},
		}
	);

	return { createNewLiquidityScenario, isCreating, createSuccess, createError}
};

// Update liquidity scenario head
export const useUpdateLiquidityScenarioHead = (liquidityScenarioId: string) => {
  const queryClient = useQueryClient();

  const { mutate: updateLiquidityScenario, isPending: isUpdating, isSuccess: updateSuccess, error: updateError } = useMutation(
    {
			mutationFn: ({ liquidityScenarioId, data }: { liquidityScenarioId: string; data: UpdateLiquidityScenarioHead }) =>
				updateLiquidityScenarioHead(liquidityScenarioId, data),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['liquidityScenarioHeads']});
        queryClient.invalidateQueries({queryKey: ['liquidityScenario', liquidityScenarioId]});
      },
    }
  );

	return { updateLiquidityScenario, isUpdating, updateSuccess, updateError };
};

// Update liquidity scenario transactions
export const useUpdateLiquidityScenarioTransactions = (clientId: string, connectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation<LiquidityScenario, Error, { liquidityScenarioId: string; transactions: LiquidityScenarioTransaction[] }>(
    {
      mutationFn: ({ liquidityScenarioId, transactions }) => updateLiquidityScenarioTransactions(liquidityScenarioId, transactions),
      onSuccess: (_, { liquidityScenarioId }) => {
        queryClient.invalidateQueries({queryKey: ['liquidityScenario', liquidityScenarioId]});
      },
    }
  );
};

// Delete a liquidity scenario
export const useDeleteLiquidityScenario = (clientId: string, connectionId: string) => {
  const queryClient = useQueryClient();

  const { mutate: deleteLiquidityScenario, isPending: isDeleting, isSuccess: deleteSuccess, error: deleteError } = useMutation(
		{
			mutationFn: (liquidityScenarioId: string) => deleteLiquidityScenarioById(liquidityScenarioId),
    	onSuccess: (_, liquidityScenarioId) => {
				queryClient.invalidateQueries({queryKey:['liquidityScenarioHeads', clientId, connectionId]});
				queryClient.removeQueries({queryKey: ['liquidityScenario', liquidityScenarioId]});
    },
  });

	return { deleteLiquidityScenario, isDeleting, deleteSuccess, deleteError };
};
